/**
 * Состав пака (WEB-3718; решения владельца 19.10 ~18:40 «воронка строится по
 * стильпаку — с его видами экрана, с настройками наших блоков и с блоками,
 * которые этому стилю свойственны. Не промптом, а кодом» и 22.10 ~22:45
 * «цветовые палитры, типы экранов и настройки блоков — полями для паков»).
 *
 * Пак (`StylePack`) задаёт ФОРМУ; состав — ЧТО обычно строят в этом стиле:
 * - `palettes` — темы кабинета с весами: палитра каждой воронки стиля сведена
 *   к ближайшей теме по цветам (фон и кнопка — с двойным весом);
 * - `fonts` — встроенные шрифты с весами;
 * - `screenKinds` — виды экрана из каталога видов (`screen-kinds.ts` на
 *   сервере) со средней долей экранов; виды, которых у нас нет, свёрнуты в
 *   замену из каталога разборщика (`parser/src/arsenal.mjs`, поле replace);
 * - `blocks` — вопросы выбора всегда, плюс блоки, которые в воронках стиля
 *   встречаются заметно чаще, чем в базе (подъём от 1,15, доля от 0,15), с
 *   настройками по большинству в собранных воронках стиля; в настройках только
 *   поведение и раскладка — цвета, шрифты и размеры даёт форма пака;
 * - `niches` — тематики, только подсказка.
 *
 * Числа — замеры разборщика (`slimkit-research/parser/база/`): воронки стиля —
 * группы `паки-v2/группы.json` по таблице PACK из `паки-v2/matrix.py` у паков,
 * у которых есть группа, иначе воронки, у которых этот пак ближайший
 * (`паки/*.json`, поле nearest); доли видов и блоков — `паки-v2/признаки.json`,
 * палитры и шрифты — `паки/*.json`. Источник каждого числа — комментарием.
 * Посчитано скриптом из отчёта `база/интеграция/СОСТАВ-ПАКА-отчёт.md`.
 *
 * Данные — каталогом `stylePackCompositionData/`, файл на пак, общий список
 * `stylePackCompositionData/index.ts` собран по каталогу (решение владельца
 * 23.10 ~04:35; сторож `style-pack-composition-files.web3718.spec.ts`, он же
 * пересобирает список). Отдельно от пака, а не полем `StylePack`: каталог паков
 * тянет ПУБЛИЧНАЯ страница покупателя (вес первого экрана, WEB-2534), а состав
 * нужен только кабинету и серверу — что он в публичную сборку не попадает,
 * держит сторож сборки (`findPublicStylePackComposition`, publicEntryGuard.ts).
 * Зеркало на сервере — `style-pack-composition-data/` с тем же устройством;
 * совпадение и каталоги имён держит сторож двух концов
 * `style-pack-composition-ends.web3718.spec.ts`.
 */
import type { StylePackComposition, StylePackId } from './stylePack.types';
import { COMPOSITION_FILES } from './stylePackCompositionData';

export const STYLE_PACK_COMPOSITIONS: Record<
  StylePackId,
  StylePackComposition
> = COMPOSITION_FILES;

export function getStylePackComposition(
  id: string,
): StylePackComposition | undefined {
  return Object.prototype.hasOwnProperty.call(STYLE_PACK_COMPOSITIONS, id)
    ? STYLE_PACK_COMPOSITIONS[id as StylePackId]
    : undefined;
}

/** Число из ключа в [0, 1): FNV-1a, тот же у сервера (`pickByWeight`). */
function unitFromKey(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h / 0x100000000;
}

/**
 * Взвешенный выбор, детерминированный по ключу (id генерации): один ключ —
 * один выбор, по многим ключам доли следуют весам.
 */
export function pickByWeight<T extends { weight: number }>(
  items: readonly T[],
  key: string,
): T | undefined {
  const total = items.reduce((s, x) => s + x.weight, 0);
  if (!items.length || total <= 0) return undefined;
  let point = unitFromKey(key) * total;
  for (const item of items) {
    point -= item.weight;
    if (point < 0) return item;
  }
  return items[items.length - 1];
}
